// =====================================================================================
// FILE: src/constants/theme.js
// =====================================================================================
// Lumen8 VenueFlow Theme - Modern, Professional, Clean
// Supports dark/light mode with backward compatibility
// =====================================================================================

// ============================================================================
// STATUS COLORS (Unified across dark/light themes)
// MatchFlow Builder: New Submission, Rejected, Approved by Lumen8, Content Unlocked
// Operator View: Ready for Testing, Ready for Playout, Rejected, Approved by Lumen8, Content Unlocked
// ============================================================================
export const STATUS_COLORS = {
  'New Submission': { 
    bg: 'rgba(161, 161, 170, 0.15)', 
    border: 'rgba(161, 161, 170, 0.5)',
    dot: '#a1a1aa',
    text: '#a1a1aa',
  },
  'Ready for Testing': { 
    bg: 'rgba(251, 146, 60, 0.18)',  // Orange - new content needs Xpression ingest
    border: 'rgba(251, 146, 60, 0.5)',
    dot: '#fb923c',
    text: '#fb923c',
  },
  'Ready for Playout': { 
    bg: 'rgba(59, 130, 246, 0.18)',  // Blue - already in Xpression
    border: 'rgba(59, 130, 246, 0.5)',
    dot: '#3b82f6',
    text: '#3b82f6',
  },
  'Rejected': { 
    bg: 'rgba(239, 68, 68, 0.2)', 
    border: 'rgba(239, 68, 68, 0.6)',
    dot: '#ef4444',
    text: '#ef4444',
  },
  'Approved by Lumen8': { 
    bg: 'rgba(34, 197, 94, 0.2)', 
    border: 'rgba(34, 197, 94, 0.6)',
    dot: '#22c55e',
    text: '#22c55e',
  },
  'Content Unlocked': { 
    bg: 'rgba(168, 85, 247, 0.18)',  // Purple - unlocked for editing
    border: 'rgba(168, 85, 247, 0.5)',
    dot: '#a855f7',
    text: '#a855f7',
  },
  // Legacy statuses (for backward compatibility with existing data)
  'Change Alert': { 
    bg: 'rgba(251, 191, 36, 0.18)', 
    border: 'rgba(251, 191, 36, 0.5)',
    dot: '#fbbf24',
    text: '#fbbf24',
  },
  'Resubmitted': { 
    bg: 'rgba(251, 146, 60, 0.18)', 
    border: 'rgba(251, 146, 60, 0.5)',
    dot: '#fb923c',
    text: '#fb923c',
  },
  'Sent to Incoder': { 
    bg: 'rgba(168, 85, 247, 0.18)', 
    border: 'rgba(168, 85, 247, 0.5)',
    dot: '#a855f7',
    text: '#a855f7',
  },
  'Example Content': { 
    bg: 'rgba(148, 163, 184, 0.1)', 
    border: 'rgba(148, 163, 184, 0.3)',
    dot: '#94a3b8',
    text: '#94a3b8',
  },
};

// ============================================================================
// BACKWARD-COMPATIBLE THEME EXPORT
// Default to light theme for MatchFlow (original behavior)
// ============================================================================
export const THEME = {
  // PRIMARY COLORS (Lumen8 Brand)
  primary: "#22BA91",
  primaryHover: "#1DA37F",
  primaryLight: "#E8F7F3",
  secondary: "#CDDE25",
  secondaryHover: "#B8C821",
  secondaryLight: "#F8FAED",
  
  // DARK ELEMENTS
  dark: "#141414",
  darkHover: "#2A2A2A",
  darkMuted: "#374151",
  
  // BACKGROUNDS
  background: "#F7F8FA",
  surface: "#FFFFFF",
  surface2: "#F3F4F6",
  
  // TEXT
  text: "#141414",
  textSecondary: "#6B7280",
  textMuted: "#9CA3AF",
  textOnDark: "#FFFFFF",
  textOnPrimary: "#FFFFFF",
  
  // BORDERS & LINES
  line: "#E5E7EB",
  lineLight: "#F3F4F6",
  lineDark: "#D1D5DB",
  
  // STATUS COLORS
  success: "#22BA91",
  successLight: "#E8F7F3",
  warning: "#F59E0B",
  warningLight: "#FEF3C7",
  error: "#EF4444",
  errorLight: "#FEE2E2",
  info: "#3B82F6",
  infoLight: "#DBEAFE",
  
  // ROW STATUS COLORS (From Operator Dashboard)
  statusColors: STATUS_COLORS,
  
  // INTERACTIVE STATES
  hover: "rgba(34, 186, 145, 0.08)",
  selected: "rgba(34, 186, 145, 0.12)",
  focus: "#22BA91",
  
  // SHADOWS
  shadowSm: "0 1px 2px rgba(0, 0, 0, 0.04)",
  shadow: "0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)",
  shadowMd: "0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.03)",
  shadowLg: "0 10px 20px rgba(0, 0, 0, 0.08), 0 4px 8px rgba(0, 0, 0, 0.04)",
  shadowXl: "0 20px 30px rgba(0, 0, 0, 0.10), 0 10px 15px rgba(0, 0, 0, 0.05)",
  shadowRow: "0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)",
  shadowRowHover: "0 4px 12px rgba(0, 0, 0, 0.10), 0 2px 4px rgba(0, 0, 0, 0.04)",
  shadowCategory: "0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04)",
  rowShadow: "0 1px 4px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)",
  rowShadowDrag: "0 8px 24px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.08)",
  
  // SPECIAL ELEMENTS
  chip: "#F3F4F6",
  chipText: "#374151",
  badge: "#CDDE25",
  badgeText: "#141414",
  
  // TABLE SPECIFIC
  tableHeader: "#F9FAFB",
  tableRowHover: "#F9FAFB",
  tableRowSelected: "rgba(34, 186, 145, 0.08)",
  tableRowAlt: "#FAFBFC",
  
  // CATEGORY COLORS
  categoryBg: "#F9FAFB",
  categoryBorder: "#E5E7EB",
  categoryText: "#374151",
  
  // TRANSITIONS
  transition: "all 0.15s ease",
  transitionFast: "all 0.1s ease",
  transitionSlow: "all 0.25s ease",
  
  // BORDER RADIUS
  radiusSm: "6px",
  radius: "10px",
  radiusMd: "12px",
  radiusLg: "16px",
  radiusXl: "20px",
  radiusFull: "9999px",
};

// ============================================================================
// FONT CONFIGURATION
// ============================================================================
export const FONTS = {
  family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
  xs: "11px",
  sm: "12px",
  base: "14px",
  md: "15px",
  lg: "16px",
  xl: "18px",
  "2xl": "20px",
  "3xl": "24px",
  tight: 1.25,
  normal: 1.5,
  relaxed: 1.625,
};

// ============================================================================
// COMMON STYLES (reusable)
// ============================================================================
export const STYLES = {
  card: {
    background: THEME.surface,
    borderRadius: THEME.radiusLg,
    boxShadow: THEME.shadow,
    border: `1px solid ${THEME.line}`,
  },
  cardElevated: {
    background: THEME.surface,
    borderRadius: THEME.radiusLg,
    boxShadow: THEME.shadowMd,
    border: "none",
  },
  input: {
    width: "100%",
    padding: "10px 14px",
    border: `1px solid ${THEME.line}`,
    borderRadius: THEME.radius,
    fontSize: FONTS.base,
    fontFamily: FONTS.family,
    fontWeight: FONTS.regular,
    color: THEME.text,
    background: THEME.surface,
    transition: THEME.transitionFast,
    outline: "none",
    boxSizing: "border-box",
  },
  buttonPrimary: {
    padding: "10px 20px",
    background: THEME.primary,
    color: THEME.textOnPrimary,
    border: "none",
    borderRadius: THEME.radiusFull,
    fontSize: FONTS.sm,
    fontFamily: FONTS.family,
    fontWeight: FONTS.semibold,
    cursor: "pointer",
    transition: THEME.transition,
    boxShadow: THEME.shadowSm,
  },
  buttonSecondary: {
    padding: "10px 20px",
    background: THEME.surface,
    color: THEME.text,
    border: `1px solid ${THEME.line}`,
    borderRadius: THEME.radiusFull,
    fontSize: FONTS.sm,
    fontFamily: FONTS.family,
    fontWeight: FONTS.semibold,
    cursor: "pointer",
    transition: THEME.transition,
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: THEME.radiusFull,
    fontSize: FONTS.xs,
    fontWeight: FONTS.semibold,
    lineHeight: 1,
  },
};

// ============================================================================
// HELPER: Get status row style (for MatchFlow rows)
// ============================================================================
export function getStatusRowStyle(status) {
  const statusStyle = STATUS_COLORS[status];
  if (!statusStyle) return {};
  return {
    background: statusStyle.bg,
    borderLeft: `3px solid ${statusStyle.border}`,
  };
}

// ============================================================================
// HELPER: Get status dot color
// ============================================================================
export function getStatusDotColor(status) {
  const statusStyle = STATUS_COLORS[status];
  return statusStyle ? statusStyle.dot : '#6b7280';
}
